import React from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import ScrollToTop from "./components/ScrollToTop";
import CookieConsent from "./components/CookieConsent";
import Layout from './components/layout/Layout';
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import PricingPage from './pages/PricingPage';
import PortfolioPage from './pages/PortfolioPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';

// Placeholder for the new home page component
/* Remove placeholder now handled by HomePage.tsx
const WebDesignHome = () => (
  <div>
    <h1>Welcome to CylostreamWeb!</h1>
    <p>This is the web design focused site.</p>
    <p>Sections to add: Services, Portfolio, Pricing (with ROI calc), About Us, Contact.</p>
    <p><a href="/original/">Link to Original Site (AI Services)</a></p>
  </div>
);
*/

const queryClient = new QueryClient();

// SEO Default values for CylostreamWeb
export const SEO_DEFAULTS = {
  titleTemplate: '%s | CylostreamWeb - Web Design & Development',
  defaultTitle: 'CylostreamWeb - Professional Web Design & Development Services',
  description: 'CylostreamWeb offers bespoke web design, development, and optimization services to elevate your online presence. Based in Belgium.',
  openGraph: {
    type: 'website',
    locale: 'en_IE',
    url: 'https://www.cylostream.com/', // Adjust if domain structure changes
    site_name: 'CylostreamWeb'
  }
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <Helmet titleTemplate={SEO_DEFAULTS.titleTemplate} defaultTitle={SEO_DEFAULTS.defaultTitle}>
        <html lang="en" />
        <meta name="description" content={SEO_DEFAULTS.description} />
        <meta property="og:type" content={SEO_DEFAULTS.openGraph.type} />
        <meta property="og:url" content={SEO_DEFAULTS.openGraph.url} />
        <meta property="og:site_name" content={SEO_DEFAULTS.openGraph.site_name} />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5, viewport-fit=cover" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="theme-color" content="#ffffff" />
      </Helmet>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Router>
          <ScrollToTop />
          <LanguageProvider>
            <ThemeProvider>
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<HomePage />} />
                  <Route path="services" element={<ServicesPage />} />
                  <Route path="pricing" element={<PricingPage />} />
                  <Route path="portfolio" element={<PortfolioPage />} />
                  <Route path="contact" element={<ContactPage />} />
                  <Route path="privacy" element={<PrivacyPolicyPage />} />
                  <Route path="terms" element={<TermsConditionsPage />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
              <CookieConsent />
            </ThemeProvider>
          </LanguageProvider>
        </Router>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
